import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import { apiConstants, getDownload } from '../../common/constants'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const actionColor = (a) => (a === 'CREATED' ? 'success' : a === 'UPDATED' ? 'primary' : 'error')

/**
 * One self-contained bulk section (CREATE or UPDATE). Each downloads its own template and uploads to
 * the mode-specific endpoint. UPDATE is all-or-nothing on the backend: any error (incl. a changed
 * locked/red column) rejects the whole file, so we surface that prominently.
 */
function UploadSection({ mode }) {
  const isUpdate = mode === 'UPDATE'
  const { enqueueSnackbar } = useSnackbar()
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tplLoading, setTplLoading] = useState(false)
  const [result, setResult] = useState(null)

  const downloadTemplate = async () => {
    setTplLoading(true)
    try {
      const res = await getDownload(`${apiConstants.BASE_LOCAL}courierPartner/bulk-template?mode=${mode}`)
      const href = URL.createObjectURL(new Blob([res.data], { type: XLSX_MIME }))
      const a = document.createElement('a')
      a.href = href
      a.download = isUpdate ? 'courier_update_template.xlsx' : 'courier_create_template.xlsx'
      a.click()
      URL.revokeObjectURL(href)
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Could not download template', { variant: 'error' })
    } finally {
      setTplLoading(false)
    }
  }

  const upload = async () => {
    if (!file) { enqueueSnackbar('Choose a file first', { variant: 'warning' }); return }
    setLoading(true)
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await axios.post(`${apiConstants.BASE_LOCAL}courierPartner/bulk-upload?mode=${mode}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res?.data?.result || res?.data?.data || res?.data?.responseObj || res?.data
      setResult(data)
      const errs = data?.errors || 0
      // UPDATE = atomic: any error means the whole file was rejected (nothing applied) → red.
      const variant = errs > 0 ? (isUpdate || !(data?.created || data?.updated) ? 'error' : 'warning') : 'success'
      enqueueSnackbar(res?.data?.meta?.message || 'Upload processed', { variant })
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Upload failed', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const errorRows = (result?.rows || []).filter((r) => r.action === 'ERROR')
  const rejected = isUpdate && result && (result.errors || 0) > 0

  return (
    <Box>
      {isUpdate ? (
        <Typography sx={{ fontSize: 13, color: '#555', mb: 1 }}>
          Edit existing couriers. The template comes <strong>pre-filled with every active courier</strong>.
          The <strong style={{ color: '#c62828' }}>red, locked columns</strong> (cp_account_code, cp_id,
          prozo_id, name, parent) are the courier's identity — <strong>do not change them</strong>. Fill/adjust
          the other columns; blank cells are left untouched. This upload is <strong>all-or-nothing</strong>: if
          any row changes a locked column (or is otherwise invalid), the <strong>entire file is rejected</strong>
          {' '}and nothing is applied.
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 13, color: '#555', mb: 1 }}>
          Create brand-new couriers using the <strong>Create Template</strong> — it includes one{' '}
          <em>example row</em> showing the expected format; replace or delete it, then add your couriers
          (the example row is ignored on upload either way). Every column is required except those marked{' '}
          <em>(optional)</em>. A row whose <code>cp_account_code</code> already exists is rejected — use the
          Update section to edit it.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
        <Button size='small' variant='outlined' startIcon={tplLoading ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />} onClick={downloadTemplate} disabled={tplLoading}>
          {isUpdate ? 'Download Update Template' : 'Download Create Template'}
        </Button>
        <Button size='small' variant='outlined' component='label' startIcon={<UploadFileOutlinedIcon />}>
          {file ? file.name : 'Choose .xlsx'}
          <input hidden type='file' accept='.xlsx,.csv' onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null) }} />
        </Button>
        <Button size='small' variant='contained' color={isUpdate ? 'warning' : 'primary'} onClick={upload} disabled={!file || loading}>
          {loading ? <CircularProgress size={18} /> : (isUpdate ? 'Upload & Update' : 'Upload & Create')}
        </Button>
      </Box>

      {result && (
        <Box>
          {rejected && (
            <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: 1, p: '8px 12px', mb: 1, fontSize: 13 }}>
              ⛔ <strong>Upload rejected — nothing was applied.</strong> {result.errors} row(s) are invalid
              (a locked column was changed, the code wasn't found, or a value is malformed). Fix the rows below and re-upload.
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip size='small' label={`Total ${result.totalRows}`} />
            {!isUpdate && <Chip size='small' color='success' label={`Created ${result.created}`} />}
            {isUpdate && <Chip size='small' color='primary' label={`Updated ${result.updated}`} />}
            <Chip size='small' color={result.errors ? 'error' : 'default'} label={`Errors ${result.errors}`} />
          </Box>
          {errorRows.length > 0 && (
            <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Courier Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>{r.cpAccountCode || '—'}</TableCell>
                      <TableCell><Chip size='small' color={actionColor(r.action)} label={r.action} /></TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {(result.errors || 0) === 0 && (
            <Typography sx={{ fontSize: 13, color: '#2e7d32' }}>
              {isUpdate ? `All ${result.updated} courier(s) updated.` : `All ${result.created} courier(s) created.`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default function BulkAttributeUploadDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Bulk Courier Attributes</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1976d2', mb: 1 }}>➕ Create new couriers</Typography>
        <UploadSection mode='CREATE' />

        <Divider sx={{ my: 2.5 }} />

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#ed6c02', mb: 1 }}>✏️ Update existing couriers</Typography>
        <UploadSection mode='UPDATE' />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
